"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import Link from "next/link";
import {
  ArrowLeft,
  Bot,
  Clipboard,
  Clock,
  Database,
  Eye,
  FileText,
  Globe,
  PencilLine,
  Search,
  Sparkles,
  TrendingUp,
  Upload,
  X,
} from "lucide-react";
import type { Guide } from "@/lib/guides-schema";
import { useReadOnly } from "@/contexts/ReadOnlyContext";
import {
  GUIDE_CATEGORY_OPTIONS,
  GUIDE_SOURCE_TYPES,
} from "@/lib/guides-schema";

type CurrentUser = {
  name: string | null;
  email: string | null;
  username: string | null;
} | null;

type SearchMode = "fts" | "semantic";

function sourceIcon(type: string) {
  switch (type) {
    case "paste":
      return Clipboard;
    case "upload":
      return Upload;
    case "url":
      return Globe;
    default:
      return FileText;
  }
}

function sourceLabel(type: string) {
  switch (type) {
    case "paste":
      return "Pasted";
    case "upload":
      return "Uploaded";
    case "url":
      return "URL";
    default:
      return type;
  }
}

export default function GuidesPage() {
  const [search, setSearch] = useState("");
  const [searchMode, setSearchMode] = useState<SearchMode>("fts");
  const [category, setCategory] = useState("");
  const [sourceType, setSourceType] = useState("");
  const [guides, setGuides] = useState<Guide[]>([]);
  const [loading, setLoading] = useState(true);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentUser, setCurrentUser] = useState<CurrentUser>(null);

  const [stats, setStats] = useState<{
    total_guides: number;
    total_categories: number;
    total_views: number;
    total_chunks: number;
    recently_added: Guide[];
    most_viewed: Guide[];
  } | null>(null);
  const [dashTab, setDashTab] = useState<"recent" | "popular">("recent");
  const isReadOnly = useReadOnly();

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchGuides = useCallback(async () => {
    setLoading(true);
    try {
      if (debouncedSearch && searchMode === "semantic") {
        const params = new URLSearchParams({ q: debouncedSearch });
        const r = await fetch(`/api/kb/semantic?${params}`);
        const data = await r.json();
        setGuides(data.guides ?? []);
      } else {
        const params = new URLSearchParams();
        if (debouncedSearch) params.set("search", debouncedSearch);
        if (category) params.set("category", category);
        if (sourceType) params.set("source_type", sourceType);
        const r = await fetch(`/api/kb?${params}`);
        const data = await r.json();
        setGuides(data.guides ?? []);
      }
    } catch {
      setGuides([]);
    }
    setLoading(false);
  }, [debouncedSearch, searchMode, category, sourceType]);

  useEffect(() => {
    fetchGuides();
  }, [fetchGuides]);

  useEffect(() => {
    fetch("/api/kb/stats")
      .then((r) => r.json())
      .then((data) => {
        if (data.error) {
          console.warn("[kb] stats error:", data.error);
          return;
        }
        setStats(data);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetch("/api/auth/me")
      .then((r) => r.json())
      .then((data: { user: CurrentUser }) => setCurrentUser(data.user ?? null))
      .catch(() => {});
  }, []);

  function canEditGuide(guide: Guide): boolean {
    if (guide.readonly || !currentUser) return false;
    const a = guide.author.trim().toLowerCase();
    return [currentUser.name, currentUser.email, currentUser.username]
      .filter((v): v is string => Boolean(v))
      .some((v) => v.trim().toLowerCase() === a);
  }

  const hasActiveFilters = category !== "" || sourceType !== "";

  function clearFilters() {
    setCategory("");
    setSourceType("");
  }

  const filterChips = useMemo(() => {
    const chips: { label: string; onRemove: () => void }[] = [];
    if (category) chips.push({ label: category, onRemove: () => setCategory("") });
    if (sourceType) chips.push({ label: sourceLabel(sourceType), onRemove: () => setSourceType("") });
    return chips;
  }, [category, sourceType]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-6xl px-6 py-8">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <Link
              href="/"
              className="inline-flex items-center gap-1.5 text-sm text-gray-500 transition hover:text-gray-800"
            >
              <ArrowLeft className="h-4 w-4" />
              Portal
            </Link>
            <div className="mt-4 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600">
                <Database className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-semibold tracking-tight text-gray-900">
                  Knowledge Base
                </h1>
                <p className="text-sm text-gray-500">{guides.length} documents</p>
              </div>
            </div>
          </div>

          <div className="mt-4 flex shrink-0 items-center gap-2">
            <Link
              href="/kb/chat"
              className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition hover:border-gray-300 hover:bg-gray-50"
            >
              <Bot className="h-4 w-4" />
              Ask AI
            </Link>
            {(currentUser || !isReadOnly) && (
              <Link
                href="/kb/new"
                className="inline-flex items-center gap-2 rounded-xl bg-gray-800 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-gray-700"
              >
                <Upload className="h-4 w-4" />
                Upload
              </Link>
            )}
          </div>
        </div>

        {/* Dashboard */}
        {stats && (
          <div className="mt-8 space-y-6">
            <div>
              <div className="flex items-center gap-6 border-b border-gray-200">
                <button
                  onClick={() => setDashTab("recent")}
                  className={`pb-2.5 text-sm font-medium transition ${
                    dashTab === "recent"
                      ? "border-b-2 border-gray-800 text-gray-900"
                      : "text-gray-400 hover:text-gray-600"
                  }`}
                >
                  <TrendingUp className="mr-1.5 inline h-3.5 w-3.5" />
                  Recently Added
                </button>
                <button
                  onClick={() => setDashTab("popular")}
                  className={`pb-2.5 text-sm font-medium transition ${
                    dashTab === "popular"
                      ? "border-b-2 border-gray-800 text-gray-900"
                      : "text-gray-400 hover:text-gray-600"
                  }`}
                >
                  <Eye className="mr-1.5 inline h-3.5 w-3.5" />
                  Most Viewed
                </button>
              </div>
              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
                {(dashTab === "recent" ? stats.recently_added : stats.most_viewed).map(
                  (guide) => {
                    const SrcIcon = sourceIcon(guide.source_type);
                    return (
                      <Link
                        key={guide.id}
                        href={`/kb/${guide.id}`}
                        className="group rounded-xl border border-gray-100 bg-white p-3.5 shadow-sm transition hover:border-gray-200 hover:shadow-md"
                      >
                        <div className="flex items-center gap-1.5 text-xs text-gray-400">
                          <SrcIcon className="h-3 w-3" />
                          <span className="rounded-md bg-gray-50 px-1.5 py-0.5 text-[11px] font-medium text-gray-500">
                            {guide.category}
                          </span>
                        </div>
                        <p className="mt-1.5 truncate text-sm font-medium text-gray-800 group-hover:text-blue-600">
                          {guide.title}
                        </p>
                        <div className="mt-2 flex items-center gap-2 text-[11px] text-gray-400">
                          {dashTab === "recent" ? (
                            <span className="inline-flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {new Date(guide.created_at).toLocaleDateString()}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1">
                              <Eye className="h-3 w-3" />
                              {(guide.view_count ?? 0).toLocaleString()} views
                            </span>
                          )}
                          {guide.read_time > 0 && (
                            <>
                              <span>·</span>
                              <span>{guide.read_time} min</span>
                            </>
                          )}
                        </div>
                      </Link>
                    );
                  },
                )}
              </div>
            </div>
          </div>
        )}

        {/* Filter bar */}
        <div className="mt-8 space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="relative flex-1">
              <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={
                  searchMode === "semantic"
                    ? "Search by meaning..."
                    : "Search title or content..."
                }
                className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-10 pr-10 text-sm text-gray-800 outline-none shadow-sm transition placeholder:text-gray-400 focus:border-blue-300 focus:ring-2 focus:ring-blue-100"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 transition hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setSearchMode("fts")}
                className={`inline-flex items-center gap-1.5 rounded-xl border px-3.5 py-2.5 text-xs font-medium shadow-sm transition ${
                  searchMode === "fts"
                    ? "border-gray-800 bg-gray-800 text-white"
                    : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                }`}
              >
                <Search className="h-3.5 w-3.5" />
                Text
              </button>
              <button
                onClick={() => setSearchMode("semantic")}
                className={`inline-flex items-center gap-1.5 rounded-xl border px-3.5 py-2.5 text-xs font-medium shadow-sm transition ${
                  searchMode === "semantic"
                    ? "border-gray-800 bg-gray-800 text-white"
                    : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                }`}
              >
                <Sparkles className="h-3.5 w-3.5" />
                AI
              </button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-xs text-gray-600 shadow-sm outline-none transition focus:border-blue-300 focus:ring-2 focus:ring-blue-100"
            >
              <option value="">All categories</option>
              {GUIDE_CATEGORY_OPTIONS.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>

            <select
              value={sourceType}
              onChange={(e) => setSourceType(e.target.value)}
              className="rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-xs text-gray-600 shadow-sm outline-none transition focus:border-blue-300 focus:ring-2 focus:ring-blue-100"
            >
              <option value="">Any source</option>
              {GUIDE_SOURCE_TYPES.map((s) => (
                <option key={s} value={s}>
                  {sourceLabel(s)}
                </option>
              ))}
            </select>

            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="inline-flex items-center gap-1 rounded-xl px-3 py-2.5 text-xs text-gray-400 transition hover:text-gray-600"
              >
                <X className="h-3.5 w-3.5" />
                Clear
              </button>
            )}
          </div>

          {filterChips.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {filterChips.map((chip) => (
                <span
                  key={chip.label}
                  className="inline-flex items-center gap-1 rounded-lg bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600"
                >
                  {chip.label}
                  <button onClick={chip.onRemove} className="hover:text-gray-800">
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="mt-8">
          {loading ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="animate-pulse rounded-2xl border border-gray-100 bg-white p-6 shadow-sm"
                >
                  <div className="mb-3 h-5 w-20 rounded-lg bg-gray-100" />
                  <div className="mb-2 h-5 w-full rounded-lg bg-gray-100" />
                  <div className="mb-4 h-4 w-3/4 rounded-lg bg-gray-50" />
                  <div className="flex gap-3">
                    <div className="h-3 w-16 rounded-lg bg-gray-50" />
                    <div className="h-3 w-12 rounded-lg bg-gray-50" />
                  </div>
                </div>
              ))}
            </div>
          ) : guides.length === 0 ? (
            <div className="rounded-2xl border border-gray-100 bg-white py-20 text-center shadow-sm">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-50">
                <Database className="h-6 w-6 text-gray-300" />
              </div>
              <p className="mt-3 text-sm text-gray-500">
                {debouncedSearch
                  ? "No documents match your search."
                  : "No documents found."}
              </p>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="mt-2 text-xs text-gray-400 transition hover:text-gray-600"
                >
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {guides.map((guide) => {
                const SourceIcon = sourceIcon(guide.source_type);
                return (
                  <Link
                    key={guide.id}
                    href={`/kb/${guide.id}`}
                    className="group flex flex-col rounded-2xl border border-gray-100 bg-white p-5 shadow-sm transition hover:border-gray-200 hover:shadow-md"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className="inline-flex items-center gap-1 rounded-lg bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-600">
                        <SourceIcon className="h-3 w-3" />
                        {guide.category}
                      </span>
                      <div className="flex shrink-0 items-center gap-2 text-gray-400">
                        <span className="inline-flex items-center gap-1 text-xs">
                          <Eye className="h-3 w-3" />
                          {(guide.view_count ?? 0).toLocaleString()}
                        </span>
                        {canEditGuide(guide) && (
                          <span
                            className="inline-flex items-center gap-0.5 rounded-lg border border-gray-200 px-1.5 py-0.5 text-xs text-gray-400 opacity-0 transition group-hover:opacity-100"
                            onClick={(e) => {
                              e.preventDefault();
                              window.location.href = `/kb/${guide.id}/edit`;
                            }}
                          >
                            <PencilLine className="h-3 w-3" />
                          </span>
                        )}
                      </div>
                    </div>

                    <h3 className="mt-3 font-medium text-gray-900 group-hover:text-blue-600">
                      {guide.title}
                    </h3>

                    {guide.summary ? (
                      <p className="mt-1.5 line-clamp-2 text-sm leading-relaxed text-gray-500">
                        {guide.summary}
                      </p>
                    ) : (
                      <p className="mt-1.5 line-clamp-2 text-sm leading-relaxed text-gray-400 italic">
                        No summary
                      </p>
                    )}

                    <div className="mt-auto flex items-center gap-3 pt-4 text-xs text-gray-400">
                      <span className="truncate">{guide.author}</span>
                      <span>·</span>
                      <span className="whitespace-nowrap">
                        {new Date(guide.updated_at).toLocaleDateString()}
                      </span>
                      {guide.read_time > 0 && (
                        <>
                          <span>·</span>
                          <span className="inline-flex items-center gap-1 whitespace-nowrap">
                            <Clock className="h-3 w-3" />
                            {guide.read_time} min
                          </span>
                        </>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
